/**
 * LineageSunburst — radial half-annulus lineage of a selected graph node.
 *
 * Cycle-fe-1 sprint-238 (S3, g238) · FR-S3.2 — sidebar LINEAGE tab content.
 * Selected node at the center with its type accent + accent ring; ancestors
 * occupy the 180°–360° half-annuli, descendants the 0°–180° half-annuli, ring
 * index = BFS depth (1..depth, default 2). Arc colours reuse `NODE_CLASSES`
 * (D3GraphCanvas) — `dim` fill / `color` stroke — so the sunburst reads
 * identically to the force graph (no invented palette). Per-ring overflow
 * collapses past 24 arcs into a "+N" chip; honest empty state when the node
 * has no lineage at all.
 *
 * Provenance: the sunburst form comes from the `Environment Lineage (Dark).png`
 * design frame (build, not port — the canonical handoff MiniMap is lane-based;
 * `LineageMiniMap` is retained untouched as the error-boundary fallback).
 *
 * Implementation note (SDD §4.3.2 deviation, flagged): arc angles are computed
 * directly over the BFS rings rather than via d3.hierarchy + d3.partition —
 * the BFS dedupes multi-path nodes to their shortest depth, so ring members
 * have no single tree parent for d3.hierarchy to partition. `d3.arc()` still
 * generates every arc path.
 */

import { Component, type ReactNode } from 'react';
import * as d3 from 'd3';
import type { D3GraphNode, D3GraphEdge } from './graph/D3GraphCanvas';
import { NODE_CLASSES } from './graph/D3GraphCanvas';
import { LineageMiniMap } from './LineageMiniMap';

function typeColour(type: string | undefined): string {
  return NODE_CLASSES[type ?? '']?.color ?? 'oklch(0.480 0.007 265)';
}
function typeDim(type: string | undefined): string {
  return NODE_CLASSES[type ?? '']?.dim ?? 'oklch(0.175 0.012 265)';
}
function edgeId(end: string | D3GraphNode): string {
  return typeof end === 'string' ? end : end.id;
}

/** One BFS ring — node ids at a single depth, shortest-path-deduped. */
export type LineageRing = string[];

/**
 * BFS up (ancestors, target→source) and down (descendants, source→target)
 * from `nodeId` over the typed edge list. Visited-set guarded (INFORMS edges
 * create cycles — a cycle-containing edge list terminates), depth-bounded,
 * first visit wins (multi-path nodes land once at their shortest depth), and
 * the selected node itself never appears in a ring.
 */
export function deriveLineageHierarchy(
  nodeId: string,
  edges: D3GraphEdge[],
  depth: number,
): { ancestors: LineageRing[]; descendants: LineageRing[] } {
  const parentsOf: Record<string, string[]> = {};
  const childrenOf: Record<string, string[]> = {};
  edges.forEach((e) => {
    const from = edgeId(e.source);
    const to = edgeId(e.target);
    (childrenOf[from] = childrenOf[from] || []).push(to);
    (parentsOf[to] = parentsOf[to] || []).push(from);
  });

  const walk = (adj: Record<string, string[]>): LineageRing[] => {
    const rings: LineageRing[] = [];
    const visited = new Set<string>([nodeId]);
    let frontier = [nodeId];
    for (let d = 1; d <= depth; d++) {
      const ring: string[] = [];
      for (const id of frontier) {
        for (const next of adj[id] || []) {
          if (visited.has(next)) continue;
          visited.add(next);
          ring.push(next);
        }
      }
      if (!ring.length) break;
      rings.push(ring);
      frontier = ring;
    }
    return rings;
  };

  return { ancestors: walk(parentsOf), descendants: walk(childrenOf) };
}

/** Max arcs rendered per ring before collapsing to a "+N" chip. */
const MAX_ARCS_PER_RING = 24;

export interface LineageSunburstProps {
  /** id of the currently-selected node (the center of the sunburst). */
  nodeId: string;
  /** the selected node — used only for the accent colour. */
  node: D3GraphNode | null;
  nodes: D3GraphNode[];
  edges: D3GraphEdge[];
  onSelect?: (id: string) => void;
  /** BFS generation depth (ring count per half), default 2. */
  depth?: 1 | 2 | 3;
}

export function LineageSunburst({ nodeId, node, nodes, edges, onSelect, depth = 2 }: LineageSunburstProps) {
  const { ancestors, descendants } = deriveLineageHierarchy(nodeId, edges, depth);

  if (!ancestors.length && !descendants.length) {
    return <div className="p-4 text-[12px] text-[var(--e-text-muted)]">No lineage recorded for this node yet.</div>;
  }

  // ── Geometry — fixed viewBox, origin-centered arcs ──
  const SIZE = 320;
  const C = SIZE / 2;
  const CENTER_R = 26;
  const RING_START = 42;
  const MAX_R = 152;
  const ringW = (MAX_R - RING_START) / depth;
  const arcGen = d3.arc();

  const byId = new Map(nodes.map((n) => [n.id, n]));
  const accent = typeColour(node?.type);

  // One half-annulus per (half, ring): descendants 0°–180°, ancestors 180°–360°.
  const halves: Array<{ rings: LineageRing[]; a0: number }> = [
    { rings: descendants, a0: 0 },
    { rings: ancestors, a0: Math.PI },
  ];

  return (
    <div className="w-full flex-1 min-h-0" data-testid="lineage-sunburst">
      <svg viewBox={`0 0 ${SIZE} ${SIZE}`} preserveAspectRatio="xMidYMid meet" className="block w-full">
        <g transform={`translate(${C} ${C})`}>
          {halves.map(({ rings, a0 }, hi) =>
            rings.map((ring, ri) => {
              const shown = ring.slice(0, MAX_ARCS_PER_RING);
              const overflow = ring.length - shown.length;
              const span = Math.PI / shown.length;
              const r0 = RING_START + ri * ringW;
              const r1 = r0 + ringW - 3;
              // "+N" chip at the ring's end (θ = a0 + π), mid-ring radius.
              const chipA = a0 + Math.PI;
              const chipR = (r0 + r1) / 2;
              return (
                <g key={`h${hi}-r${ri}`}>
                  {shown.map((id, i) => {
                    const n = byId.get(id);
                    const d = arcGen({
                      innerRadius: r0,
                      outerRadius: r1,
                      startAngle: a0 + i * span,
                      endAngle: a0 + (i + 1) * span,
                      padAngle: 0.012,
                    });
                    if (!d) return null;
                    return (
                      <path
                        key={id}
                        d={d}
                        data-node-id={id}
                        fill={typeDim(n?.type)}
                        stroke={typeColour(n?.type)}
                        strokeOpacity={0.85}
                        strokeWidth={1}
                        style={{ cursor: 'pointer' }}
                        onClick={() => onSelect?.(id)}
                      >
                        <title>{n?.label ?? id}</title>
                      </path>
                    );
                  })}
                  {overflow > 0 && (
                    <text
                      x={chipR * Math.sin(chipA)}
                      y={-chipR * Math.cos(chipA) + 3}
                      textAnchor="middle"
                      className="font-mono"
                      fontSize="9"
                      fill="var(--e-text-muted)"
                      letterSpacing="0.04em"
                    >
                      +{overflow}
                    </text>
                  )}
                </g>
              );
            }),
          )}

          {/* Center — selected node, accent fill + accent ring */}
          <circle r={CENTER_R + 4} fill="none" stroke={accent} strokeOpacity="0.45" strokeWidth="1" />
          <circle r={CENTER_R} fill={typeDim(node?.type)} stroke={accent} strokeWidth="1.25">
            <title>{node?.label ?? nodeId}</title>
          </circle>
        </g>
      </svg>
    </div>
  );
}

// ── Error boundary — MiniMap fallback (FR-S3.2) ─────────────────────────────

interface LineageSunburstBoundaryProps {
  nodeId: string;
  node: D3GraphNode | null;
  nodes: D3GraphNode[];
  edges: D3GraphEdge[];
  onSelect?: (id: string) => void;
  children: ReactNode;
}

interface LineageSunburstBoundaryState {
  hasError: boolean;
  lastNodeId: string | null;
}

/**
 * Renders the lane-based `LineageMiniMap` (same props contract) when the
 * sunburst throws. Error state resets when the selected node changes.
 */
export class LineageSunburstBoundary extends Component<LineageSunburstBoundaryProps, LineageSunburstBoundaryState> {
  state: LineageSunburstBoundaryState = { hasError: false, lastNodeId: null };

  static getDerivedStateFromError(): Partial<LineageSunburstBoundaryState> {
    return { hasError: true };
  }

  static getDerivedStateFromProps(
    props: LineageSunburstBoundaryProps,
    state: LineageSunburstBoundaryState,
  ): Partial<LineageSunburstBoundaryState> | null {
    if (props.nodeId !== state.lastNodeId) {
      return { hasError: false, lastNodeId: props.nodeId };
    }
    return null;
  }

  componentDidCatch(): void {
    // Fallback to the MiniMap is the recovery — nothing further to do here;
    // React already logs the error in development.
  }

  render() {
    if (this.state.hasError) {
      const { nodeId, node, nodes, edges, onSelect } = this.props;
      return <LineageMiniMap nodeId={nodeId} node={node} nodes={nodes} edges={edges} onSelect={onSelect} />;
    }
    return this.props.children;
  }
}
